import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={3} smOffset={5} className="table-header">
      <Button
        className="relative-padding"
        bsStyle="primary"
        type="submit"
        onClick={props.addButtonClick}
      >
        Add Industry
      </Button>
    </Col>
  </Row>
);

const TableHeader = () => (
  <div>
    <Row>
      <Col sm={4} className="table-header">
        Industry name
      </Col>
      <Col sm={3} className="table-header">
        Date created
      </Col>
      <Col sm={3} className="table-header">
        Date updated
      </Col>
      <Col sm={2} className="table-header">
        Action
      </Col>
    </Row>
    <hr />
  </div>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Insight Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const industry = props.item;
  return (
    <Row key={industry.id}>
      <Col sm={4} className="table-entry">
        {industry.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(industry.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(industry.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        <Button
          className="relative-padding"
          bsStyle="link"
          onClick={() => props.removeItem(industry)}
          aria-label={`Remove ${industry.name}`}
        >
          Remove
        </Button>
      </Col>
    </Row>
  );
};

export default class ShapeSetIndustries extends React.Component {
  static displayName = 'ShapeSetIndustries';

  constructor(props) {
    super(props);
    this.state = {
      newIndustryId: undefined,
      showNewIndustry: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    this.getIndustries();
    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getIndustries = () => {
    // make API call to get industries for this shapeSet
    const shapeSetId = parseInt(this.props.params.shapeSetId, 10);
    this.props.getShapeSetIndustries(shapeSetId, true);
    this.props.getShapeSetIndustries(shapeSetId, false);
  };

  removeIndustry = industry => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const shapeSetId = parseInt(this.props.params.shapeSetId, 10);
    this.props
      .removeIndustryFromShapeSet(shapeSetId, industry)
      .then(response => this.success(response), error => this.failure(error));
  };

  addIndustry = () => {
    if (this.state.newIndustryId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const shapeSetId = parseInt(this.props.params.shapeSetId, 10);
      const newIndustryId = this.state.newIndustryId;
      this.props
        .addIndustryToShapeSet(shapeSetId, newIndustryId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewIndustry: false, newIndustryId: undefined });
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    this.setState({ showNewIndustry: !this.state.showNewIndustry });
  };

  cancel = () => {
    this.setState({ showNewIndustry: false, newIndustryId: undefined });
  };

  selectOnChange = newIndustryId => {
    this.setState({ newIndustryId });
  };

  render() {
    const { allocated, unallocated } = this.props.currentShapeSet.industries;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeIndustry} />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing industries for catchment set {this.props.currentShapeSet.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <TableHeader />

        {this.state.showNewIndustry && (
          <AddNewRow
            items={unallocated}
            addItem={this.addIndustry}
            selectValue={this.state.newIndustryId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="industries"
          tableHeader={TableHeader}
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/shapeset/ShapeSetIndustries.js